import { useMutation, useQuery, useQueryClient } from 'react-query';

import axiosClient from '../../api/axios-client';
import { BASE_MESSAGERIE_MENAGE } from '../../api/housekeepingMessaging';
import type { BoiteMenage, FilMenage, ListeFilsMenage, MessageMenage, PageMessages, PersonneMenage, ReponseRapide, TypeFil } from '../../types/housekeepingMessaging';

/**
 * Les lectures et gestes de la messagerie interne du ménage — chantier Beaucour.
 *
 * Pas de temps réel côté mobile (le socle n'embarque pas Echo) : la liste se relit
 * toutes les 30 s, un fil ouvert toutes les 8 s. L'établissement est dans la clé,
 * comme partout au mobile : une bascule d'hôtel ne sert jamais le fil précédent.
 */

export { REPONSES_RAPIDES } from '../../types/housekeepingMessaging';
export type {
    BoiteMenage,
    TypeFil,
    ReponseRapide,
    PersonneMenage,
    MessageMenage,
    FilMenage,
    ListeFilsMenage,
    PageMessages,
} from '../../types/housekeepingMessaging';

export interface EnvoiMessage {
    filId: number;
    body?: string;
    audio?: { blob: Blob; extension: string } | null;
    photo?: File | null;
    room_id?: number | null;
    quick_reply_key?: ReponseRapide | null;
    is_urgent?: boolean;
    reply_to_message_id?: number | null;
    private?: boolean;
}

const BASE = BASE_MESSAGERIE_MENAGE;

export const cleFils = (facilityId: number | null) => ['hk-messaging-threads', facilityId] as const;
export const cleMessages = (facilityId: number | null, filId: number) => ['hk-messaging-messages', facilityId, filId] as const;

export function useFilsMenage(facilityId: number | null, actif = true) {
    return useQuery(
        cleFils(facilityId),
        async () => (await axiosClient.get<ListeFilsMenage>(`${BASE}/threads`)).data,
        { enabled: actif && facilityId !== null, refetchInterval: 30_000, refetchOnWindowFocus: true },
    );
}

export function useMessagesFil(facilityId: number | null, filId: number) {
    return useQuery(
        cleMessages(facilityId, filId),
        async () => (await axiosClient.get<PageMessages>(`${BASE}/threads/${filId}/messages`)).data,
        { enabled: facilityId !== null && filId > 0, refetchInterval: 8_000, refetchOnWindowFocus: true },
    );
}

export async function ouvrirFil(data: {
    kind: TypeFil;
    box?: BoiteMenage;
    member_user_id?: number;
    other_facility_id?: number;
}): Promise<FilMenage> {
    return (await axiosClient.post<FilMenage>(`${BASE}/threads`, data)).data;
}

export function useEnvoyerMessage(facilityId: number | null) {
    const client = useQueryClient();

    return useMutation(
        async ({ filId, audio, photo, ...reste }: EnvoiMessage) => {
            // Un seul envoi, vocal et photo compris : ils vont sur le disque privé du
            // serveur, jamais dans `/files` que tout l'hôtel lit.
            const formulaire = new FormData();
            for (const [cle, valeur] of Object.entries(reste)) {
                if (valeur === null || valeur === undefined) continue;
                formulaire.append(cle, typeof valeur === 'boolean' ? (valeur ? '1' : '0') : String(valeur));
            }
            if (audio) formulaire.append('audio', new File([audio.blob], `vocal.${audio.extension}`, { type: audio.blob.type }));
            if (photo) formulaire.append('photo', photo);

            return (await axiosClient.post<MessageMenage>(`${BASE}/threads/${filId}/messages`, formulaire)).data;
        },
        {
            onSuccess: (message, { filId }) => {
                void client.invalidateQueries(cleMessages(facilityId, filId));
                // Une réponse privée part dans un autre fil que celui affiché.
                if (message.thread_id !== filId) void client.invalidateQueries(cleMessages(facilityId, message.thread_id));
                void client.invalidateQueries(cleFils(facilityId));
            },
        },
    );
}

export { lireLecteurs, marquerFilLu, masquerMessage, signalerFilFerme, signalerFilOuvert } from '../../api/housekeepingMessaging';
export type { LecteurMessage } from '../../api/housekeepingMessaging';

export async function enregistrerModeMuet(muet: boolean): Promise<void> {
    await axiosClient.put(`${BASE}/preferences`, { quiet_off_duty: muet });
}
